import { useState } from "react";

export default function PeptidesVsPharmaceuticals() {
  return (
    <section id="vs-pharmaceuticals" className="mb-16 scroll-mt-20">
      <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
        Peptides vs. Conventional Pharmaceuticals
      </h2>
      
      <p className="text-lg mb-8">
        Traditional pharmaceuticals and peptide therapeutics represent fundamentally 
        different approaches to medicine. Understanding these differences helps explain 
        why peptides are generating such excitement in medical research and clinical practice.
      </p>
      
      {/* Interactive Comparison Tool */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
        <div className="bg-[#2C3E50] text-white p-4">
          <h3 className="font-serif text-xl font-semibold">
            Comparison: "Blunt Force" vs. "Biomimetic" Action
          </h3>
        </div>
        
        <div className="p-6 overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#EBF5FB]">
                <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Characteristic</th>
                <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Conventional Pharmaceuticals</th>
                <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Peptide Therapeutics</th>
              </tr>
            </thead>
            <tbody>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 border-b border-gray-200 font-semibold">Mode of Action</td>
                <td className="py-3 px-4 border-b border-gray-200">Often block receptors or inhibit enzymes; "off-switch" approach</td>
                <td className="py-3 px-4 border-b border-gray-200">Mimic natural signals; modulate pathways up or down</td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 border-b border-gray-200 font-semibold">Target Specificity</td>
                <td className="py-3 px-4 border-b border-gray-200">May bind to multiple targets ("shotgun" approach)</td>
                <td className="py-3 px-4 border-b border-gray-200">High specificity for intended targets ("key in lock")</td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 border-b border-gray-200 font-semibold">Side Effects</td>
                <td className="py-3 px-4 border-b border-gray-200">Often substantial due to off-target binding</td>
                <td className="py-3 px-4 border-b border-gray-200">Typically fewer due to high specificity</td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 border-b border-gray-200 font-semibold">Relationship to Biology</td>
                <td className="py-3 px-4 border-b border-gray-200">Introduce novel chemical structures</td>
                <td className="py-3 px-4 border-b border-gray-200">Use/mimic the body's own molecules</td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 border-b border-gray-200 font-semibold">Metabolic Fate</td>
                <td className="py-3 px-4 border-b border-gray-200">Often produce metabolites requiring liver processing</td>
                <td className="py-3 px-4 border-b border-gray-200">Typically break down into amino acids</td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="py-3 px-4 font-semibold">Response Type</td>
                <td className="py-3 px-4">Linear dose-response; may override feedback loops</td>
                <td className="py-3 px-4">Often produce transient, tunable responses</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Visual Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#7F8C8D] p-3">
            <h4 className="font-serif text-lg font-semibold text-white">Conventional Pharmaceutical</h4>
          </div>
          <div className="p-6">
            <div className="flex justify-center mb-6">
              <svg className="h-48 w-full" viewBox="0 0 300 200">
                <rect x="70" y="50" width="160" height="100" rx="10" fill="#f5f5f5" stroke="#d1d1d1" strokeWidth="2"/>
                <circle cx="150" cy="100" r="30" fill="#E74C3C"/>
                <path d="M120 100 L180 100" stroke="#333" strokeWidth="15" strokeLinecap="round"/>
                <text x="150" y="170" textAnchor="middle" fill="#333" fontWeight="bold">BLOCKADE ACTION</text>
                <text x="150" y="30" textAnchor="middle" fill="#555" fontSize="14">Conventional Drug</text>
              </svg>
            </div>
            <p className="mb-4"><strong>Metaphor:</strong> Like using a sledgehammer to turn off a light switch.</p>
            <p>
              Traditional pharmaceuticals often work by completely blocking a receptor or 
              shutting down an enzyme. This can effectively stop a problem (like pain or 
              inflammation) but may disrupt related pathways, leading to side effects.
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="bg-[#3498DB] p-3">
            <h4 className="font-serif text-lg font-semibold text-white">Peptide Therapeutic</h4>
          </div>
          <div className="p-6">
            <div className="flex justify-center mb-6">
              <svg className="h-48 w-full" viewBox="0 0 300 200">
                <rect x="70" y="50" width="160" height="100" rx="10" fill="#f5f5f5" stroke="#d1d1d1" strokeWidth="2"/>
                <path d="M120,100 Q150,70 180,100" stroke="#3498DB" strokeWidth="5" fill="none"/>
                <path d="M120,100 Q150,130 180,100" stroke="#3498DB" strokeWidth="5" fill="none"/>
                <circle cx="120" cy="100" r="8" fill="#3498DB"/>
                <circle cx="180" cy="100" r="8" fill="#3498DB"/>
                <text x="150" y="170" textAnchor="middle" fill="#333" fontWeight="bold">MODULATION ACTION</text>
                <text x="150" y="30" textAnchor="middle" fill="#555" fontSize="14">Peptide Therapy</text>
              </svg>
            </div>
            <p className="mb-4"><strong>Metaphor:</strong> Like having a conversation with a cell in its own language.</p>
            <p>
              Peptides typically work by mimicking the body's natural signals. Rather than 
              shutting down a system, they can dial it up or down, allowing for more nuanced 
              regulation of biological processes.
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-[#ECF0F1] p-6 rounded-lg">
        <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Clinical Implications</h3>
        <p className="mb-4">The biomimetic approach of peptides offers several potential advantages in clinical settings:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Reduced risk of adverse reactions</li>
          <li>Better tolerability in long-term treatment</li>
          <li>More physiological responses</li>
          <li>Potential for combination therapies with fewer drug interactions</li>
        </ul>
      </div>
    </section>
  );
}
